import {
  Box,
  Center,
  Container,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  VStack,
} from '@chakra-ui/react';
import { useCreateRoastMutation } from 'hooks/mutations/roast/useCreateRoastMutation';
import {
  type RoastPeriod,
  useGetAllRoastsQuery,
} from 'hooks/queries/roast/useGetAllRoastsQuery';
import { useGetRoastsQuery } from 'hooks/queries/roast/useGetRoastsQuery';
import { useScrollToTop } from 'hooks/useScrollToTop';
import { Fragment, useEffect, useRef, useState } from 'react';

import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import PageLoader from 'components/PageLoader';
import { ProtectedElement } from 'components/ProtectedElement';
import { SEO } from 'components/SEO';
import Tabs from 'components/Tabs';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';

import RoastListCard from './components/RoastListCard';

const BoostPromoCard = () => (
  <Box
    borderRadius="xl"
    border="1px solid"
    borderColor="rgba(139,92,246,0.15)"
    bgGradient="linear(to-r, rgba(139,92,246,0.05), rgba(249,115,22,0.05))"
    p={4}
    width="100%"
  >
    <HStack justifyContent="space-between" flexWrap="wrap" spacing={3}>
      <VStack spacing={0.5} alignItems="flex-start">
        <Text fontSize="small" fontWeight="semibold">
          Promote your project to 25,000+ developers
        </Text>
        <Text fontSize="micro" opacity="tertiary">
          Get featured on our homepage, newsletter, and social channels
        </Text>
      </VStack>
      <Button
        scheme="outline"
        size="sm"
        onClick={() => window.open('https://bigdevsoon.me/boost/', '_blank')}
      >
        Boost
      </Button>
    </HStack>
  </Box>
);

const PERIOD_OPTIONS: { label: string; value: RoastPeriod | undefined }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Yearly', value: 'yearly' },
  { label: 'All Time', value: undefined },
];

const CASCADE_ORDER: RoastPeriod[] = ['daily', 'weekly', 'monthly', 'yearly'];

/** Compact submit card — shown below existing roasts on My Roasts tab */
const RoastUrlInputCompact = () => {
  const [url, setUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { mutate: createRoast, isPending } = useCreateRoastMutation();

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = () => {
    if (!url.trim()) return;
    createRoast(
      { url: url.trim() },
      { onSuccess: () => setUrl('') }
    );
  };

  return (
    <Box
      p={5}
      borderRadius="xl"
      border="1px solid"
      borderColor="rgba(249,115,22,0.2)"
      bgGradient="linear(to-r, rgba(249,115,22,0.06), transparent)"
      width="100%"
    >
      <HStack spacing={2} mb={3}>
        <Box
          width="24px"
          height="24px"
          borderRadius="md"
          backgroundColor="orange.500"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          <Box as="span" color="white" fontSize="12px" lineHeight={1}>🔥</Box>
        </Box>
        <Text fontSize="small" fontWeight="semibold">
          Submit another project
        </Text>
      </HStack>
      <HStack width="100%" spacing={3}>
        <InputGroup size="md" flex={1}>
          <InputLeftElement pointerEvents="none">
            <Box as="span" color="whiteAlpha.400" fontSize="14px">🌐</Box>
          </InputLeftElement>
          <Input
            ref={inputRef}
            placeholder="https://yourproject.com"
            value={url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter') handleSubmit();
            }}
            borderRadius="lg"
            backgroundColor="whiteAlpha.200"
            border="1px solid"
            borderColor="whiteAlpha.200"
            _hover={{ borderColor: 'whiteAlpha.300' }}
            _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px var(--chakra-colors-orange-500)' }}
            _placeholder={{ color: 'whiteAlpha.400' }}
          />
        </InputGroup>
        <ProtectedElement
          scheme="gradient"
          size="md"
          onClick={handleSubmit}
          isLoading={isPending}
          isDisabled={!url.trim()}
          requiredTokens={3}
        >
          Roast
        </ProtectedElement>
      </HStack>
      <Text fontSize="xMicro" opacity="tertiary" mt={2}>
        HTTPS only · Public landing pages · No auth-protected routes
      </Text>
    </Box>
  );
};

/** Hero-style submit — shown as the empty state on My Roasts tab */
const RoastUrlInputHero = () => {
  const [url, setUrl] = useState('');
  const { mutate: createRoast, isPending } = useCreateRoastMutation();
  const { isMobile } = useMediaQueries();
  const heroInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => heroInputRef.current?.focus(), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = () => {
    if (!url.trim()) return;
    createRoast(
      { url: url.trim() },
      { onSuccess: () => setUrl('') }
    );
  };

  return (
    <Center width="100%" mt={isMobile ? 8 : '8vh'}>
      <VStack spacing={6} maxWidth="560px" width="100%" px={2}>
        {/* Fire icon */}
        <Box
          width="48px"
          height="48px"
          borderRadius="xl"
          bgGradient="linear(to-br, orange.500, red.500)"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box as="span" color="white" fontSize="20px" lineHeight={1}>🔥</Box>
        </Box>

        {/* Heading + description */}
        <VStack spacing={2} textAlign="center">
          <Text fontSize="xLarge" fontWeight="semibold">
            Roast My Project
          </Text>
          <Text fontSize="small" opacity="secondary">
            AI delivers brutally honest feedback across 8 categories.
            Scored out of 100. Not for the faint-hearted.
          </Text>
        </VStack>

        {/* URL input */}
        <VStack spacing={2} width="100%">
          <HStack width="100%" spacing={3}>
            <InputGroup size="lg" flex={1}>
              <InputLeftElement pointerEvents="none">
                <Box as="span" color="whiteAlpha.400" fontSize="16px">🌐</Box>
              </InputLeftElement>
              <Input
                ref={heroInputRef}
                placeholder="https://yourproject.com"
                value={url}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') handleSubmit();
                }}
                borderRadius="xl"
                backgroundColor="whiteAlpha.200"
                border="1px solid"
                borderColor="whiteAlpha.200"
                _hover={{ borderColor: 'whiteAlpha.300' }}
                _focus={{ borderColor: 'orange.500', boxShadow: '0 0 0 1px var(--chakra-colors-orange-500)' }}
                _placeholder={{ color: 'whiteAlpha.400' }}
              />
            </InputGroup>
            <ProtectedElement
              scheme="gradient"
              size="lg"
              onClick={handleSubmit}
              isLoading={isPending}
              isDisabled={!url.trim()}
              requiredTokens={3}
            >
              Roast
            </ProtectedElement>
          </HStack>
          <Text fontSize="xMicro" opacity="tertiary">
            HTTPS only · Public landing pages · No auth-protected routes
          </Text>
        </VStack>
      </VStack>
    </Center>
  );
};

const Roasts = () => {
  useScrollToTop();
  const [tabIndex, setTabIndex] = useState(0);
  const [period, setPeriod] = useState<RoastPeriod | undefined>('daily');
  const [isCascading, setIsCascading] = useState(true);
  const { id: currentUserId } = useCurrentUser();
  const { isMobile } = useMediaQueries();

  const { data: allRoasts, isPending: isAllPending } = useGetAllRoastsQuery({ limit: 50, period });
  const { data: myRoasts, isPending: isMyPending } = useGetRoastsQuery({ userId: currentUserId });

  // Cascade: when a period returns no ready roasts, try the next one
  useEffect(() => {
    if (!isCascading || isAllPending) return;

    const hasReady = allRoasts && allRoasts.some((r) => r.status === 'ready');

    if (hasReady) {
      setIsCascading(false);
      return;
    }

    // No ready roasts — try next period
    if (period) {
      const currentIdx = CASCADE_ORDER.indexOf(period);
      const nextPeriod = CASCADE_ORDER[currentIdx + 1];
      if (nextPeriod) {
        setPeriod(nextPeriod);
      } else {
        // Exhausted all periods, fall back to All Time
        setPeriod(undefined);
        setIsCascading(false);
      }
    } else {
      setIsCascading(false);
    }
  }, [allRoasts, isAllPending, isCascading, period]);

  const renderAllRoasts = () => {
    if ((isAllPending || isCascading) && (!allRoasts || allRoasts.length === 0)) {
      return <PageLoader shift />;
    }

    if (!allRoasts || allRoasts.length === 0) {
      return (
        <Center width="100%" mt={isMobile ? 10 : '12vh'}>
          <VStack spacing={3}>
            <Text fontSize="xLarge" opacity="secondary">
              No roasts yet.
            </Text>
            <Text fontSize="small" opacity="tertiary">
              Be the first to submit a project!
            </Text>
          </VStack>
        </Center>
      );
    }

    // Sort by engagement: ready roasts ranked by (upvotes*3 + comments*2 + score*1), processing at bottom
    const sorted = [...allRoasts].sort((a, b) => {
      if (a.status === 'ready' && b.status !== 'ready') return -1;
      if (a.status !== 'ready' && b.status === 'ready') return 1;
      if (a.status !== 'ready' && b.status !== 'ready') return 0;
      const scoreA = (a.upvote_count ?? 0) * 3 + (a.comment_count ?? 0) * 2 + (a.scores?.overall ?? 0);
      const scoreB = (b.upvote_count ?? 0) * 3 + (b.comment_count ?? 0) * 2 + (b.scores?.overall ?? 0);
      return scoreB - scoreA;
    });

    let readyCount = 0;
    return (
      <VStack spacing={3} mt={4} width="100%">
        {sorted.map((roast, idx) => {
          if (roast.status === 'ready') readyCount++;
          const rank = roast.status === 'ready' ? readyCount : undefined;
          return (
            <Fragment key={roast.id}>
              <RoastListCard roast={roast} rank={rank} />
              {idx === 2 && <BoostPromoCard />}
            </Fragment>
          );
        })}
      </VStack>
    );
  };

  const renderMyRoasts = () => {
    if (isMyPending && (!myRoasts || myRoasts.length === 0)) {
      return <PageLoader shift />;
    }

    if (!myRoasts || myRoasts.length === 0) {
      return <RoastUrlInputHero />;
    }

    return (
      <VStack spacing={3} mt={4} width="100%">
        {myRoasts.map((roast) => (
          <RoastListCard key={roast.id} roast={roast} isMine />
        ))}
        <RoastUrlInputCompact />
      </VStack>
    );
  };

  return (
    <Container maxW="1400px" width="100%">
      <SEO
        title="BigDevSoon | Roast My Project"
        description="AI-powered project roasting with scores across 8 categories. Get brutally honest feedback on your project."
        url={`${process.env.REACT_APP_SITE_URL}roasts`}
      />
      <VStack alignItems="flex-start" width="100%" spacing={4}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          background="gray.900"
          width="100%"
          py={3}
        >
          <Text fontSize="xxLarge" fontWeight="semibold">
            Roast My Project
          </Text>
          <Button
            scheme="primary"
            size="sm"
            onClick={() => setTabIndex(1)}
          >
            + New Roast
          </Button>
        </Stack>
        <HStack spacing={4} width="100%" justifyContent="space-between" flexWrap="wrap">
          <Tabs
            tabs={['All Roasts', 'My Roasts']}
            tabIndex={tabIndex}
            onChange={(index) => setTabIndex(index)}
            selectedColor="orange.600"
          />
          {tabIndex === 0 && (
            <HStack spacing={1}>
              {PERIOD_OPTIONS.map((opt) => (
                <Box
                  key={opt.label}
                  as="button"
                  px={3}
                  py={1.5}
                  borderRadius="md"
                  fontSize="12px"
                  fontWeight="medium"
                  cursor="pointer"
                  transition="all 0.15s"
                  backgroundColor={period === opt.value ? 'orange.600' : 'whiteAlpha.100'}
                  color={period === opt.value ? 'white' : 'whiteAlpha.600'}
                  _hover={{
                    backgroundColor: period === opt.value ? 'orange.500' : 'whiteAlpha.200',
                    color: 'white',
                  }}
                  onClick={() => {
                    setIsCascading(false);
                    setPeriod(opt.value);
                  }}
                >
                  {opt.label}
                </Box>
              ))}
            </HStack>
          )}
        </HStack>
      </VStack>
      {tabIndex === 0 ? renderAllRoasts() : renderMyRoasts()}
    </Container>
  );
};

export default Roasts;
